// SHADOW de @plone/volto/components/manage/Blocks/Teaser/DefaultBody.jsx
// Renderiza o Teaser com cara AgSUS DS (flat: imagem → título → descrição).
// Data de publicação em cor neutra; CTA "Ler matéria completa" OPCIONAL
// (toggle data.showCta no schema, default off — protótipo não mostra botão).
import React from 'react';
import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import cx from 'classnames';
import config from '@plone/volto/registry';

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
  readMore: {
    id: 'Ler matéria completa',
    defaultMessage: 'Ler matéria completa',
  },
});

// Formata data do effective (ISO string) como "28 mar 2026". Aceita
// effective_date variantes vindas do Plone. Retorna null se invalido.
const formatPtDate = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  // sentinela do Plone pra "sem data publicacao"
  if (iso.startsWith('1969-') || iso.startsWith('1970-')) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
    .format(d)
    .replace('.', '');
};

const TeaserDefaultTemplate = (props) => {
  const { className, data, isEditMode, style } = props;
  const intl = useIntl();
  const href = data.href?.[0];
  const image = data.preview_image?.[0];
  const url = data.preview_image?.[0]?.['@id'];

  const Image = config.getComponent('Image').component;
  const { openExternalLinkInNewTab } = config.settings;

  // Eyebrow: head_title manual > data formatada do conteudo referenciado
  const autoDate = href ? formatPtDate(href.effective || href.EffectiveDate) : null;
  const eyebrow = data?.head_title || autoDate;

  return (
    <div className={cx('block teaser ag-featured-card', className)} style={style}>
      <>
        {!href && isEditMode && (
          <Message>
            <div className="teaser-item placeholder">
              <Image src={imageBlockSVG} alt="" />
              <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
            </div>
          </Message>
        )}
        {href && (
          <MaybeWrap
            condition={!isEditMode}
            as={UniversalLink}
            href={href['@id']}
            target={
              data.openLinkInNewTab ||
              (openExternalLinkInNewTab && !isInternalURL(href['@id']))
                ? '_blank'
                : null
            }
            className="ag-featured-card__link"
          >
            <article className="teaser-item default ag-featured-card__card">
              {url && !image?.image_field ? (
                <div className="image-wrapper ag-featured-card__img">
                  <Image src={url} alt="" loading="lazy" responsive={true} />
                </div>
              ) : (
                (href.hasPreviewImage || href.image_field || image) && (
                  <div className="image-wrapper ag-featured-card__img">
                    <Image
                      item={image || href}
                      imageField={image ? image.image_field : href.image_field}
                      alt=""
                      loading="lazy"
                      responsive={true}
                    />
                  </div>
                )
              )}
              <div className="content ag-featured-card__body">
                {eyebrow && (
                  <div className="headline ag-featured-card__date">
                    {eyebrow}
                  </div>
                )}
                <h2 className="ag-featured-card__title">{data?.title}</h2>
                {!data.hide_description && (
                  <p className="ag-featured-card__desc">{data?.description}</p>
                )}
                {data.showCta && (
                  <div className="ag-featured-card__cta">
                    <span className="ag-btn ag-btn--primary">
                      {intl.formatMessage(messages.readMore)}
                      <svg
                        className="ag-featured-card__arrow"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </span>
                  </div>
                )}
              </div>
            </article>
          </MaybeWrap>
        )}
      </>
    </div>
  );
};

TeaserDefaultTemplate.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditMode: PropTypes.bool,
};

export default TeaserDefaultTemplate;
